import { filter, keyBy, mapValues } from 'lodash';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';

import agentsResource from 'src/actions/agent/agentsResource';
import campaignsResource, { CampaignType } from 'src/actions/campaigns/campaignsResource';
import { toDateTimeString } from 'src/presentation/components/pages/shared/utils/TimeUtils';
import useResource, {
  getConsolidatedState,
  someFailed,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
} from 'src/presentation/hooks/useResource';
import { toastError } from 'src/actions/toast';

const useProactiveCommunication = ({ campaignType }: { campaignType: CampaignType }) => {
  const dispatch = useDispatch();

  const { values: agents, loadingState: agentsLoadingState } = useResource(agentsResource);
  const {
    errors: campaignsErrors,
    loadingState: campaignsLoadingState,
    values: campaigns,
  } = useResource(campaignsResource);

  const usersMap = useMemo(() => mapValues(keyBy(agents, 'id')), [agents]);

  const loadingStates = [agentsLoadingState, campaignsLoadingState];

  const fetchError = someFailed(...loadingStates);

  const loadingState = fetchError ? STATUS_FAILED : getConsolidatedState(loadingStates);

  const campaignsExtended = useMemo(() => {
    if (campaignsLoadingState !== STATUS_SUCCEEDED) {
      return [];
    }
    return filter(campaigns, campaign => campaign.type === campaignType).map(campaign => ({
      ...campaign,
      createdAtString: toDateTimeString(campaign.createdAt),
      createdByString: usersMap[campaign?.createdBy?.id]?.name,
    }));
  }, [campaigns, campaignType, campaignsLoadingState, usersMap]);

  useEffect(() => {
    if (fetchError) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [fetchError, dispatch]);

  return { campaigns: campaignsExtended, campaignsErrors, loadingState };
};

export default useProactiveCommunication;
